import { Controller } from "@hotwired/stimulus";

// Pill-group toggle controller. Three modes (via data-mode):
//   "multi"     — multiple pills selectable, submits as Array (name="key[]").
//   "tri-state" — exactly one pill active; radio behavior with neutral default.
//                 Submits as SCALAR (name="key") — date_preset / is_returning_attendee
//                 / qualified / has_notes are scalar URL params per PERMITTED_PARAM_SHAPE.
//   "hour-grid" — multi-select of hour cells + quick-pick pills (Morning,
//                 Afternoon, Evening) that toggle ranges. Submits as Array.
//
// v3 array-params migration (2026-05-26 — Task 19):
//   • Submission hidden inputs are now PER-PILL (`name="key[]"`) for multi/hour-grid.
//   • `data-hours` on the pill-group root is the SEED channel for initial state
//     (server-rendered, integer-only, internal) — STAYS CSV. Only URL submission
//     migrated to Array shape.
//   • `keyName` resolves from `keyValue` (new, per Task 22 partial rewrite) with
//     a fallback to `nameValue` for backward compat during the migration window.
//
// Markup contract (post-T22):
//   <div data-controller="filter-pill-group"
//        data-filter-pill-group-mode-value="multi"
//        data-filter-pill-group-key-value="quality_band">
//     <button class="filter-pill" data-filter-pill-group-target="pill"
//             data-action="filter-pill-group#toggle" data-value="buyer">Buyer</button>
//     <button class="filter-pill" data-filter-pill-group-target="pill"
//             data-action="filter-pill-group#toggle" data-value="warm">Warm</button>
//     <!-- per-pill hidden inputs appended dynamically by _syncHidden -->
//   </div>
//
// For hour-grid:
//   <div data-controller="filter-pill-group" data-filter-pill-group-mode-value="hour-grid"
//        data-filter-pill-group-key-value="scan_hour"
//        data-hours="9,10,11">  <!-- server-rendered CSV seed for initial state -->
//     <button data-action="filter-pill-group#quickPick" data-hours="6,7,8,9,10,11">Morning</button>
//     ... 24 cells ...
//     <button class="filter-hour-grid__cell" data-action="filter-pill-group#toggleHour" data-hour="0">00</button>
//   </div>
export default class extends Controller {
  static targets = ["hidden", "pill"];
  static values = { mode: String, name: String, key: String };

  connect() {
    this._syncFromHidden();
    // BUG #11 — restore on Reset / preset-load / reloadDraft. filter_diff
    // dispatches "filter-diff:values-applied" with the FULL flat hash; rebuild
    // this group's selection from detail.values[key] (Array/scalar ⇒ select;
    // absent ⇒ clear).
    //
    // The event is dispatched on the DRAWER ROOT (#leads-filter-drawer, which
    // carries data-controller="filter-diff"). This pill-group renders INSIDE
    // .filter-drawer__body — i.e. a DESCENDANT of that root — so a bubbling
    // event never reaches a listener on this.element (bubbling goes UP only).
    // Bind on an ancestor that actually receives it: the offcanvas / filter-diff
    // root if resolvable, else document (the event always bubbles up to it).
    this._valuesAppliedHandler = (event) =>
      this._applyValues(event.detail && event.detail.values);
    this._vaRoot =
      this.element.closest(".offcanvas") ||
      this.element.closest('[data-controller~="filter-diff"]') ||
      document;
    this._vaRoot.addEventListener("filter-diff:values-applied", this._valuesAppliedHandler);
  }

  disconnect() {
    if (this._valuesAppliedHandler && this._vaRoot) {
      this._vaRoot.removeEventListener("filter-diff:values-applied", this._valuesAppliedHandler);
    }
  }

  // Resolve the URL param key. Prefer `keyValue` (new, set by T22 partial rewrite);
  // fall back to `nameValue` for backward compat with pre-T22 partials.
  get keyName() {
    return this.keyValue || this.nameValue || "";
  }

  // tri-state submits as a SCALAR URL param (name="key"), not Array.
  // Per PERMITTED_PARAM_SHAPE in v3 plan L317–320.
  get _submitsArray() {
    return this.modeValue !== "tri-state";
  }

  // ─── multi-select mode ────────────────────────────────────────────────────
  toggle(event) {
    if (this.modeValue === "tri-state") return this._triState(event);
    const btn = event.currentTarget;
    btn.classList.toggle("is-active");
    btn.setAttribute("aria-pressed", btn.classList.contains("is-active") ? "true" : "false");
    this._syncHidden();
  }

  // ─── tri-state mode (radio-like, default = neutral) ───────────────────────
  _triState(event) {
    const btn = event.currentTarget;
    const isDefault = btn.dataset.value === "" || btn.dataset.default === "true";
    Array.from(this.element.querySelectorAll(".filter-pill")).forEach((p) => {
      p.classList.remove("is-active", "is-default-selected");
      p.setAttribute("aria-pressed", "false");
    });
    btn.classList.add(isDefault ? "is-default-selected" : "is-active");
    btn.setAttribute("aria-pressed", "true");
    this._syncHidden();
  }

  // ─── hour-grid: per-hour toggle ───────────────────────────────────────────
  toggleHour(event) {
    const cell = event.currentTarget;
    const pressed = cell.getAttribute("aria-pressed") === "true";
    cell.setAttribute("aria-pressed", pressed ? "false" : "true");
    this._syncHidden();
  }

  // ─── hour-grid: quick-pick ────────────────────────────────────────────────
  quickPick(event) {
    // v3 — data-hours on a quick-pick button is the range definition (e.g. "6,7,8,9,10,11"
    // for Morning). This is an internal CSV channel between button attr and JS — NOT a URL
    // param. It STAYS CSV. Only the submission hidden inputs migrate to Array shape.
    const initialHours = (event.currentTarget.dataset.hours || "").split(",").map((s) => s.trim()).filter(Boolean);
    const cells = this.element.querySelectorAll(".filter-hour-grid__cell");
    const allActive = initialHours.every((h) => {
      const cell = Array.from(cells).find((c) => c.dataset.hour === h);
      return cell?.getAttribute("aria-pressed") === "true";
    });
    // If all hours in the range are already active, toggle them OFF; else turn them all ON.
    initialHours.forEach((h) => {
      const cell = Array.from(cells).find((c) => c.dataset.hour === h);
      if (cell) cell.setAttribute("aria-pressed", allActive ? "false" : "true");
    });
    this._syncHidden();
  }

  // Centralized hidden-input writer. SINGLE SOURCE OF TRUTH for this group's
  // submission inputs (BUG #6).
  //
  // For multi + hour-grid: strips EVERY prior submission input this group owns
  //   — per-pill `data-pill-key` markers, the server-rendered ARRAY inputs (now
  //   tagged `data-pill-key` post-#7 too), AND any legacy target-marked input
  //   that slipped through — then appends one `<input type="hidden" name="key[]"
  //   value="v">` per active pill. NEVER writes a comma-joined CSV element into
  //   a `key[]` array input (the BUG #6 poison: quality_band[]="warm,hot").
  // For tri-state: emits a single scalar `<input type="hidden" name="key"
  //   value="v">` (or none if neutral default).
  _syncHidden() {
    if (!this.keyName) return;

    // Strip ALL submission inputs this group owns so no duplicate/stale input
    // survives. Match by data-pill-key, by name === `${key}[]` (covers a
    // server-rendered array input regardless of marker), and by the legacy
    // target marker. Keys are simple identifiers (no CSS.escape — that helper
    // is also absent in the jsdom test env).
    this._ownedHiddenInputs().forEach((el) => el.remove());

    const values = this._collectActiveValues();

    if (this._submitsArray) {
      // multi + hour-grid: per-pill Array submission — one input per value.
      values.forEach((value) => {
        this.element.appendChild(this._buildHidden(`${this.keyName}[]`, value));
      });
    } else {
      // tri-state: SCALAR submission. Reuse the server-rendered hidden target if
      // present (sole source of truth for the scalar param); else create one.
      const scalar = values[0] || "";
      if (this.hasHiddenTarget) {
        this.hiddenTarget.value = scalar;
      } else {
        this.element.appendChild(this._buildHidden(this.keyName, scalar));
      }
    }

    this._syncQuickPicks();
    this._dispatchInput();
  }

  // LRPO-5507 (item 7) — a quick-pick pill ("Morning" / "Afternoon" / "Evening")
  // is pressed iff EVERY hour in its range is pressed in the grid. quickPick()
  // used to flip the CELLS and nothing else, so the pill the user had just
  // clicked stayed visually inert — and a page load with those hours applied left
  // it inert too.
  //
  // Deriving the pill state FROM the grid (rather than tracking it at the click)
  // means one code path covers every route into the same state: clicking the
  // pill, ticking cells by hand, a server-rendered page load, a preset load, a
  // Reset. It also un-presses the pill correctly the moment the user unticks a
  // single hour out of the range, which a click-tracking flag could not.
  _syncQuickPicks() {
    if (this.modeValue !== "hour-grid") return;

    const pressed = new Set(
      Array.from(this.element.querySelectorAll('.filter-hour-grid__cell[aria-pressed="true"]'))
        .map((cell) => cell.dataset.hour)
    );

    this.element.querySelectorAll('[data-action*="quickPick"][data-hours]').forEach((btn) => {
      const hours = (btn.dataset.hours || "").split(",").map((s) => s.trim()).filter(Boolean);
      const on = hours.length > 0 && hours.every((h) => pressed.has(h));
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    });
  }

  // Every submission input this group manages: per-pill markers, server-rendered
  // ARRAY inputs (name === `${key}[]`), and any legacy target-marked input.
  // EXCLUDES the tri-state scalar hidden target (it is the sole source of truth
  // and is mutated in place, not removed).
  _ownedHiddenInputs() {
    const all = Array.from(this.element.querySelectorAll('input[type="hidden"]'));
    const arrayName = `${this.keyName}[]`;
    return all.filter((el) => {
      if (this._submitsArray && this.hasHiddenTarget && this.hiddenTargets.includes(el)) {
        // (Defense-in-depth) a legacy target-marked ARRAY input must be removed.
        return el.name === arrayName;
      }
      return el.dataset.pillKey === this.keyName || el.name === arrayName;
    });
  }

  _buildHidden(name, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    input.dataset.pillKey = this.keyName;
    return input;
  }

  // DOM-derived active values (single source of truth — preserves existing
  // controller semantics where pill state lives on the DOM via `.is-active`
  // class and `aria-pressed="true"`).
  _collectActiveValues() {
    if (this.modeValue === "hour-grid") {
      return Array.from(this.element.querySelectorAll('.filter-hour-grid__cell[aria-pressed="true"]'))
        .map((c) => c.dataset.hour)
        .filter(Boolean);
    }
    if (this.modeValue === "tri-state") {
      const active = this.element.querySelector(".filter-pill.is-active, .filter-pill.is-default-selected");
      const v = active?.dataset.value;
      // Treat the default-neutral pill (empty value) as "no value submitted".
      return v ? [v] : [];
    }
    // multi
    return Array.from(this.element.querySelectorAll(".filter-pill.is-active"))
      .map((p) => p.dataset.value)
      .filter(Boolean);
  }

  _dispatchInput() {
    // Dispatch on the root so filter-diff (parent) sees changes regardless of
    // whether a per-pill or single hidden input is emitted.
    this.element.dispatchEvent(new Event("input", { bubbles: true }));
    this.element.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // BUG #11 — rebuild the WHOLE group (pressed visual state + hidden inputs)
  // from filter_diff's "filter-diff:values-applied" payload. detail.values is
  // the FULL flat hash; for this group we read values[key]:
  //   • Array  → press exactly those pills/cells.
  //   • scalar → press that single pill (tri-state) / that one cell.
  //   • undefined / null (key absent) → CLEAR.
  // Reuses the existing pressed-state mechanism and _syncHidden so there is one
  // code path for "selection → DOM → hidden inputs".
  _applyValues(values) {
    if (!this.keyName) return;
    const raw = values ? values[this.keyName] : undefined;
    const wanted = new Set(
      (Array.isArray(raw) ? raw : raw == null ? [] : [raw]).map(String)
    );

    if (this.modeValue === "hour-grid") {
      this.element.querySelectorAll(".filter-hour-grid__cell").forEach((cell) => {
        cell.setAttribute("aria-pressed", wanted.has(cell.dataset.hour) ? "true" : "false");
      });
    } else if (this.modeValue === "tri-state") {
      // Reset every pill, then press the matching one (or the neutral default
      // when the value is absent/empty).
      const pills = Array.from(this.element.querySelectorAll(".filter-pill"));
      pills.forEach((p) => {
        p.classList.remove("is-active", "is-default-selected");
        p.setAttribute("aria-pressed", "false");
      });
      const value = wanted.values().next().value; // first (and only) value, or undefined
      const target = pills.find((p) => (p.dataset.value || "") === (value || "")) ||
                     pills.find((p) => (p.dataset.value || "") === "" || p.dataset.default === "true");
      if (target) {
        const isDefault = (target.dataset.value || "") === "" || target.dataset.default === "true";
        target.classList.add(isDefault ? "is-default-selected" : "is-active");
        target.setAttribute("aria-pressed", "true");
      }
    } else {
      // multi
      this.element.querySelectorAll(".filter-pill").forEach((p) => {
        const on = wanted.has(p.dataset.value);
        p.classList.toggle("is-active", on);
        p.setAttribute("aria-pressed", on ? "true" : "false");
      });
    }

    // Regenerate hidden inputs from the freshly-set DOM state (single path).
    this._syncHidden();
  }

  // When the drawer is initialized with applied params, hidden state is
  // server-set. Sync visual state from BOTH:
  //   1. `data-hours` on the root (CSV seed channel — integer-only, internal).
  //   2. Any `hiddenTargets` server-rendered (legacy single CSV input, pre-T22)
  //      or per-pill hidden inputs (post-T22 or post-user-interaction).
  _syncFromHidden() {
    // Channel 1 — server-rendered seed on the root (CSV, integer-only).
    const fromData = (this.element.dataset.hours || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    // Channel 2 — hidden inputs. During the migration window, partials still
    // emit a single CSV input via data-filter-pill-group-target="hidden".
    // Post-T22, partials will emit per-pill hidden inputs (no target marker
    // needed — _syncHidden manages them by data-pill-key). Read both.
    const fromInputs = [];
    if (this.hasHiddenTarget) {
      this.hiddenTargets.forEach((el) => {
        const v = el.value || "";
        // If the legacy single-input CSV value is non-empty, split it.
        if (v.includes(",")) {
          v.split(",").map((s) => s.trim()).filter(Boolean).forEach((s) => fromInputs.push(s));
        } else if (v) {
          fromInputs.push(v);
        }
      });
    }
    // Also pick up any per-pill / server-rendered ARRAY hidden inputs already in
    // the DOM (e.g., from a prior _syncHidden call or the server render). Match
    // by data-pill-key marker or by name === `${key}[]`. Filter keys are simple
    // identifiers, so no CSS.escape — that helper is also absent under jsdom.
    if (this.keyName) {
      this._ownedHiddenInputs().forEach((el) => { if (el.value) fromInputs.push(el.value); });
    }

    const active = new Set([...fromData, ...fromInputs]);

    if (this.modeValue === "hour-grid") {
      this.element.querySelectorAll(".filter-hour-grid__cell").forEach((cell) => {
        cell.setAttribute("aria-pressed", active.has(cell.dataset.hour) ? "true" : "false");
      });
      // Page load with hours already applied: the grid is seeded above, but the
      // quick-pick pills are not — _syncFromHidden does NOT route through
      // _syncHidden, so it has to press them itself (LRPO-5507 item 7).
      this._syncQuickPicks();
    } else if (this.modeValue === "tri-state") {
      // tri-state: exactly one value at most. Pick the first active match.
      const target = Array.from(this.element.querySelectorAll(".filter-pill"))
        .find((p) => active.has(p.dataset.value || ""));
      if (target) {
        const isDefault = (target.dataset.value || "") === "" || target.dataset.default === "true";
        target.classList.add(isDefault ? "is-default-selected" : "is-active");
        target.setAttribute("aria-pressed", "true");
      }
    } else {
      // multi
      this.element.querySelectorAll(".filter-pill").forEach((p) => {
        if (active.has(p.dataset.value)) {
          p.classList.add("is-active");
          p.setAttribute("aria-pressed", "true");
        }
      });
    }
  }
}
